<template>
	<component
		:is="portal ? 'portal' : 'span'"
		to="pop"
	>
		<helio-pop
			:parent-position="parentPosition"
			:parent-class="popClassName"
			:active="active"
			:anchor="anchor"
			:close-on-outside-click="true"
			:arrow="true"
			:initial-position="position"
			:initial-offset="offset"
			:gap="gap"
			:skin="skin"
			:width="width"
			class="tooltip"
			shadow="small"
			role="status"
			aria-relevant="additions text"
			aria-live="polite"
			aria-atomic="false"
			@close="$emit('close')"
			@open="$emit('open')"
		>
			<template slot="content">
				<slot name="content" />
			</template>
		</helio-pop>
	</component>
</template>

<script>
import Pop from '../pop/Pop.vue';

export default {
	name: 'Tooltip',

	components: {
		'helio-pop': Pop,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
			description: 'Sets whether the Tooltip is displayed',
		},

		anchor: {
			type: String,
			required: true,
			description: 'Sets which element will control the Tooltip\'s position',
		},

		gap: {
			type: Number,
			required: false,
			default: 12,
			description: 'Sets the space in px between the anchor and the point of the Tooltip',
		},

		offset: {
			type: String,
			required: false,
			default: 'center',
			validator: value => [
				'bottom',
				'center',
				'left',
				'right',
				'top',
			].indexOf(value) !== -1,
			description: 'Sets the offset of the Tooltip in relation to its anchor',
		},

		parentPosition: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'fixed',
				'normal',
			].indexOf(value) !== -1,
			description: 'Provides context about the parent\'s position. This context determines how the Pop component will position the Tooltip.',
		},

		portal: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the Tooltip will be nested in a portal named "pop" so it can be sent elsewhere in the DOM',
		},

		position: {
			type: String,
			required: false,
			default: 'top',
			validator: value => [
				'left',
				'bottom',
				'top',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets the position of the Tooltip in relation to its anchor',
		},

		skin: {
			type: String,
			required: false,
			default: 'light',
			validator: value => [
				'dark',
				'light',
			].indexOf(value) !== -1,
			description: 'Sets whether the Tooltip will have a dark or light skin',
		},

		width: {
			type: Number,
			required: false,
			default: null,
			description: 'Sets a custom width for the Tooltip',
		},
	},

	slots: {
		default: {
			required: true,
			scoped: false,
			description: 'Supplies the content of the Tooltip',
		},
	},

	data() {
		return {
			popClassName: '',
		};
	},

	mounted() {
		if (this.$el && this.$el.classList && this.$el.classList.value) {
			this.popClassName = this.$el.classList.value.replace('v-portal', '');
		}
	},
};
</script>

<style lang="scss" src="./tooltip.scss" />
